import React from 'react';
import { useTranslation } from 'react-i18next';

import { Shapes, MediaDevices, General } from 'icons/untitledui';

import ErrorBoundary from 'layout/components/ErrorBoundary';

import useWindowSize from 'shared/hooks/useWindowResize';
import { useShiftNoteCapture } from 'shiftNotes/helpers/ShiftNoteCaptureProvider';
import { readStoredInputMethod } from 'settings/utils/inputMethodStorage';

import * as Buttons from 'theme/components/Buttons.style';
import * as Styled from 'shiftNotes/styles/NoteCaptureReady.style';

import breakpoints from 'theme/atoms/breakpoints';

export interface NoteCaptureReadyProps {
  mobileView?: boolean;
}

export default function NoteCaptureReady({
  mobileView,
}: NoteCaptureReadyProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  const {
    isRecording,
    onRecordingToggle,
    openWritingMode,
    clearActiveGuidanceQuestion,
  } = useShiftNoteCapture();

  const [windowWidth] = useWindowSize();
  const isDesktopView =
    !mobileView && windowWidth > parseInt(breakpoints.tablet, 10);

  const defaultsToKeyboard = readStoredInputMethod() === 'keyboard';

  const ariaLabel = isRecording
    ? t('currentShift.Stop recording')
    : t('currentShift.Start recording');

  const handleButtonClick = () => {
    clearActiveGuidanceQuestion();
    if (defaultsToKeyboard && !isRecording) {
      openWritingMode();
      return;
    }
    onRecordingToggle();
  };

  const getButtonIcon = () => {
    if (isRecording) return <Shapes.Square stroke={'white'} />;
    if (defaultsToKeyboard) return <General.Plus />;
    return <MediaDevices.Microphone01 />;
  };

  if (isDesktopView) {
    return (
      <ErrorBoundary>
        <Buttons.Primary
          $size="large"
          stretch
          onClick={handleButtonClick}
          aria-label={ariaLabel}
          tabIndex={0}
          data-testid="note-capture-record-button"
        >
          {getButtonIcon()}
        </Buttons.Primary>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Styled.ButtonContainer $mobileView={mobileView}>
        <Styled.RoundMicButton
          type="button"
          onClick={handleButtonClick}
          aria-label={ariaLabel}
          data-testid="note-capture-record-button"
        >
          {getButtonIcon()}
        </Styled.RoundMicButton>
      </Styled.ButtonContainer>
    </ErrorBoundary>
  );
}
